"use client"

import { ampli } from "@/ampli"
import { Button } from "@/elements"
import { ButtonSize } from "@/elements/Button/types"
import { FragmentType, graphql, useFragment } from "@/gql"
import { useMessagingToken } from "@/hooks/useMessagingToken"
import { decodeGraphqlNodeId } from "@/tracking"
import type { NovaSession } from "@/types"
import clsx from "clsx"
import React, { FC, forwardRef, useState } from "react"
import { Dialog, DialogContent, DialogTrigger } from "../../Dialog"
import { MessageModalContentWrapper } from "./MessageModalContentWrapper"
import { MessageModalLoading } from "./MessageModalLoading"

const MessageModalFragment = graphql(`
  fragment MessageModal on User {
    id
    uuid

    creative {
      id
    }

    ...MessageModalContentWrapper
  }
`)

interface Props {
  user: FragmentType<typeof MessageModalFragment> | undefined | null
  serverSession: NovaSession
  applicationId?: string
  fillSpace?: boolean
  jobId?: string
  trackingEntryPoint: string
  Trigger?: FC<{ onClick: (e: React.MouseEvent<HTMLElement>) => void }>
}

const DefaultTrigger = forwardRef<
  HTMLButtonElement,
  { buttonSize?: ButtonSize; fillSpace?: boolean; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void }
>(({ onClick, fillSpace = false }, ref) => {
  return (
    <Button ref={ref} onClick={onClick} className={clsx(fillSpace && "w-full")}>
      Message
    </Button>
  )
})

DefaultTrigger.displayName = "DefaultTrigger"

export const MessageModal: FC<Props> = props => {
  const user = useFragment(MessageModalFragment, props.user)
  const { serverSession, fillSpace, Trigger = DefaultTrigger, trackingEntryPoint } = props
  const [isOpen, setIsOpen] = useState(false)
  const messagingToken = useMessagingToken()

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Trigger
          fillSpace={fillSpace}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()

            ampli.chatStart({
              user_id: [decodeGraphqlNodeId(user?.id).numericalId || ""],
              entry_point: trackingEntryPoint,
            })

            setIsOpen(true)
          }}
        />
      </DialogTrigger>
      <DialogContent
        zIndex={102}
        className="min-w-fit"
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
        }}>
        <div className="relative flex h-screen w-screen flex-col overflow-hidden sm:h-[875px] sm:w-[600px] sm:rounded-xl sm:max-modal-size">
          {messagingToken ? (
            <MessageModalContentWrapper
              messagingToken={messagingToken}
              user={user}
              jobId={props.jobId}
              trackingEntryPoint={trackingEntryPoint}
              closeModal={() => setIsOpen(false)}
              serverSession={serverSession}
              applicationId={props.applicationId}
            />
          ) : (
            <MessageModalLoading />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
